import { EntriFlowStatus, EntriFlowErrorType } from '@wix/domains-serverless-interface';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { getPremiumEssentials } from '@wix/premium-fed-sdk/@premium-essentials';
import { getEntriWhiteLabelConfig } from './entriWhiteLabelConfig';
import { fetchWithFallback } from '@wix/premium-fed-sdk/@apis-infra';
import React from 'react';
import { EntriModule } from './EntriModule';
import { EntriIntroModalModule } from './EntriIntroModal/EntriIntroModalModule';
import { ModalsModule } from '../../../ModalsModule';
import { waitForEntriIframeReady } from './waitForEntriIframeReady';
export const startEntriFlow = async _ref => {
  let {
    domain,
    siteName,
    metaSiteId,
    skipIntro,
    enabledBySpec,
    overrideEntriEligibility,
    disablePointing,
    fullPage = false,
    useCustomExitConfirmation = false,
    t
  } = _ref;
  const {
    loaderModal
  } = ModalsModule.internals.domainsSharedModalsController;
  const {
    httpClient,
    i18n
  } = getPremiumEssentials();
  const {
    language
  } = i18n;
  const locale = language ?? 'en';
  const {
    entriConfig,
    experiments
  } = DomainsApiClient.queries({
    httpClient: httpClient
  });
  const entriQueries = EntriModule.internals.backend.queries({
    httpClient: httpClient
  });
  const domainName = domain.input ? domain.fullAsciiDomain : domain;
  try {
    var _await$fetchWithFallb;
    loaderModal.show({});
    let entriConfigResponse = overrideEntriEligibility;
    if (!entriConfigResponse) {
      entriConfigResponse = await entriConfig({
        disablePointing,
        domain
      });
    }
    const {
      checkDomainData,
      config,
      isEligible
    } = entriConfigResponse;
    EntriModule.internals.bi.onIsEligibleForFlow == null || EntriModule.internals.bi.onIsEligibleForFlow({
      domainName,
      isEligible: !!isEligible
    });
    const isSpecEnabled = enabledBySpec && isEligible ? (_await$fetchWithFallb = (await fetchWithFallback(experiments({
      experiments: [enabledBySpec]
    }), {})).experiments) == null ? void 0 : _await$fetchWithFallb[enabledBySpec] : true;
    loaderModal.hide();
    if (!isEligible || !isSpecEnabled) {
      return {
        status: EntriFlowStatus.manual,
        config,
        checkDomainData
      };
    }
    const whiteLabelConfig = getEntriWhiteLabelConfig({
      t,
      locale,
      fullPage,
      useCustomExitConfirmation
    });
    const startAutomaticFlow = async function (_temp) {
      let {
        showLoaderModal = true
      } = _temp === void 0 ? {} : _temp;
      if (showLoaderModal) {
        loaderModal.show({});
      }
      EntriModule.internals.bi.onEntriStart == null || EntriModule.internals.bi.onEntriStart({
        domainName,
        siteName,
        provider: checkDomainData == null ? void 0 : checkDomainData.provider,
        setupType: checkDomainData == null ? void 0 : checkDomainData.setupType
      });
      const result = await entriQueries.entriStart({
        domain,
        whiteLabelConfig,
        msid: metaSiteId,
        skipConnection: overrideEntriEligibility == null ? void 0 : overrideEntriEligibility.isEligible,
        entriConfigOverride: overrideEntriEligibility,
        disablePointing,
        fullPage
      });
      if (showLoaderModal) {
        loaderModal.hide();
      }
      EntriModule.internals.bi.onEntriExit == null || EntriModule.internals.bi.onEntriExit({
        domainName,
        siteName,
        result
      });
      return result;
    };
    return new Promise(resolve => {
      if (skipIntro) {
        // Full-page embeds render Entri in their own container, so the loader
        // modal would sit beneath the iframe for the whole session. Mirror the
        // intro's full-page path and skip it (the host shows its own loader
        // until the iframe covers it).
        return resolve(startAutomaticFlow({
          showLoaderModal: !fullPage
        }));
      }
      const {
        emptyModal
      } = ModalsModule.internals.domainsSharedModalsController;
      emptyModal.show({
        ...(fullPage && {
          screen: 'full',
          fullPage
        }),
        content: /*#__PURE__*/React.createElement(EntriIntroModalModule.SDK.EntriIntroModal, {
          domain: domain,
          siteName: siteName,
          onRequestClose: emptyModal.hide,
          fullPage: fullPage,
          onContinue: async () => {
            if (fullPage) {
              const entriReady = waitForEntriIframeReady();
              const flowResult = startAutomaticFlow({
                showLoaderModal: false
              });
              await entriReady;
              emptyModal.hide();
              resolve(flowResult);
              return;
            }
            emptyModal.hide();
            resolve(startAutomaticFlow());
          },
          onManualFlow: () => {
            emptyModal.hide();
            resolve({
              status: EntriFlowStatus.manual,
              checkDomainData,
              config,
              errorType: EntriFlowErrorType.userChoseManual
            });
          }
        })
      });
    });
  } catch (err) {
    loaderModal.hide();
    const result = {
      status: EntriFlowStatus.manual,
      error: (err == null ? void 0 : err.message) ?? `unknown error: ${err}`,
      errorType: EntriFlowErrorType.setupFailed
    };
    EntriModule.internals.bi.onEntriExit == null || EntriModule.internals.bi.onEntriExit({
      domainName,
      siteName,
      result
    });
    return result;
  }
};
//# sourceMappingURL=startEntriFlow.js.map